import React from "react";
import { Box, Typography, Container, Link, Chip, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import ApiRoundedIcon from "@mui/icons-material/ApiRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";

interface SummaryProps {
  refProp: React.RefObject<HTMLDivElement>;
}

const Summary: React.FC<SummaryProps> = ({ refProp }) => {
  const { t } = useTranslation("", { keyPrefix: "Newmove.Summary" });

  const whatItems = [
    { key: "what1", icon: <CheckCircleOutlineRoundedIcon sx={{ color: "#56CB75", mt: "2px", flexShrink: 0 }} /> },
    { key: "what2", icon: <CancelOutlinedIcon sx={{ color: "#f44336", mt: "2px", flexShrink: 0 }} /> },
    { key: "what3", icon: <SettingsSuggestRoundedIcon sx={{ color: "#FFC040", mt: "2px", flexShrink: 0 }} /> },
    { key: "what4", icon: <ApiRoundedIcon sx={{ color: "#2F7BFB", mt: "2px", flexShrink: 0 }} /> },
  ] as const;

  return (
    <Container ref={refProp} data-aos="fade-up" sx={{ pt: 8, pb: { xs: 4, md: 10 } }}>
      {/* Outer card */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: 4,
          boxShadow: "0 4px 32px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Amber top accent bar */}
        <Box sx={{ height: 5, background: "linear-gradient(90deg, #FFC040 0%, #ffab00 100%)" }} />

        <Box sx={{ py: { xs: 5, md: 8 }, px: { xs: 3, sm: 5, md: 8 } }}>

          {/* Badge + title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Chip
              icon={<WarningAmberRoundedIcon />}
              label={t("badge")}
              size="small"
              sx={{
                backgroundColor: "#FFF3CD",
                color: "#856404",
                fontWeight: 700,
                fontSize: "0.75rem",
                "& .MuiChip-icon": { color: "#FFC040" },
              }}
            />
          </Box>

          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 800, mb: 1, fontSize: { xs: "1.6rem", md: "2.2rem" }, lineHeight: 1.25 }}
          >
            {t("title")}
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.secondary", mb: 4 }}>
            {t("greeting")}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          {/* Body paragraphs */}
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 2, lineHeight: 1.8 }}>
            {t("paragraph1")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 2, lineHeight: 1.8 }}>
            {t("paragraph2")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 5, lineHeight: 1.8 }}>
            {t("paragraph3")}
          </Typography>

          {/* Key Date Callout */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              background: "linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)",
              border: "1.5px solid #FFC040",
              borderRadius: 3,
              px: { xs: 3, md: 5 },
              py: 3,
              mb: 5,
            }}
          >
            <CalendarTodayRoundedIcon sx={{ fontSize: 36, color: "#ffab00", flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#856404", textTransform: "uppercase", letterSpacing: 1 }}>
                {t("shutdownLabel")}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#7a4f00", lineHeight: 1.2 }}>
                {t("shutdownDate")}
              </Typography>
            </Box>
          </Box>

          {/* What does this mean */}
          <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 3 }}>
            {t("whatTitle")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 5 }}>
            {whatItems.map(({ key, icon }) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  backgroundColor: "#F9F9F9",
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                }}
              >
                {icon}
                <Box>
                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                    {t(key)}
                  </Typography>
                  {key === "what3" && (
                    <Link
                      href="https://docs.newmove.energy/docs/newmove-one/konfiguration#standalone-modus"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 1,
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        color: "#FFC040",
                        "&:hover": { color: "#ffab00" },
                      }}
                    >
                      {t("what3LinkText")} →
                    </Link>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Contact section */}
          <Box
            sx={{
              border: "1.5px solid",
              borderColor: "divider",
              borderRadius: 3,
              px: { xs: 3, md: 5 },
              py: 3,
            }}
          >
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {t("questionsTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.7 }}>
              {t("questionsText")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <EmailRoundedIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Link
                href={`mailto:${t("email")}`}
                underline="hover"
                sx={{ fontWeight: 700, color: "primary.dark", fontSize: "1rem" }}
              >
                {t("email")}
              </Link>
            </Box>
            <Typography variant="body2" sx={{ color: "text.tertiary" }}>
              {t("closingText")}
            </Typography>
          </Box>

        </Box>
      </Box>
    </Container>
  );
};

export default Summary;

